import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { setLoader } from 'redux/slices/loader';
import {
  getCurrentSubscription,
  getPaymentMethods,
  markDefaultCard,
  refreshSubscription,
  removeCard,
  sendSupportEmail,
} from 'services/subscriptionService';
import useFetchPlans from 'hooks/subscriptions/fetchPlans';
import { getAvailableLifetimePlans } from 'services/lifetimeSubscriptionService';
import PaymentCard from './PaymentCard';
import sc from './Subscription.module.scss';
import { CONFIRM_SUBSCRIPTION } from 'constants';
import PlanDetailCard from 'Components/Pages/BillingAndSubscriptions/PlanDetailCard';
import TrialFeatures from 'Components/Pages/BillingAndSubscriptions/TrialFeatures';
import SubscriptionCardModal from './SubscriptionCardModal';
import CustomCheckbox from 'Components/Form/CustomCheckbox';
import OrderSummary from 'Components/Pages/BillingAndSubscriptions/OrderSummary';
import BottomDrawer from 'Components/Drawer/BottomDrawer';
import SubscriptionScreen from './Subscription';
// import SubscriptionCardModal from './SubscriptionCardModal';
import SubscriptionCardForm from './SubscriptionForm';
// import OnboardingHeader from 'Components/Onboarding/Header/OnboardingHeader';
import OnboardingHeader from 'Components/Pages/Onboarding/Header/Header';
import { resetOnboarding } from 'redux/slices/newOnboarding';
import { TbExclamationCircle } from 'react-icons/tb';

const BillingSectionHeading = ({ heading, desc, className, subDesc }) => {
  return (
    <div className={`${sc.billingSectionHeadingWrapper} ${className}`}>
      <h3 className={`${sc.heading} ${sc.headingDetails}`}>{heading}</h3>
      {desc && <p className={sc.desc}>{desc}</p>}
    </div>
  );
};

const PaymentScreen = ({ productData = {}, setOrderSummary }) => {
  const authState = useSelector((state) => state.auth);
  // Use the same pattern as ProtectedRoute for consistency
  const user = authState?.user || {};
  const isLoggedIn = authState?.isLoggedIn;
  const [selectedCard, setSelectedCard] = useState();
  const [showMore, setShowMore] = useState(1);
  const [paymentMethods, setPaymentMethods] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const [selectedPlanType, setSelectedPlanType] = useState('month');
  const [newSubscription, setNewSubscription] = useState(null);
  const [isDisabledSwitch, setIsDisabledSwitch] = useState(false);
  const [planFeatures, setPlanFeatures] = useState([]);
  const [lastSubscription, setLastSubscription] = useState();
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const fetchPlansData = useFetchPlans();
  const paymentIntent = searchParams?.get('payment_intent');
  const trial = searchParams?.get('trial') || false;
  const isLifetime = searchParams?.get('lifetime') === 'true';

  // Handle authentication for lifetime payment flow
  useEffect(() => {
    if (isLifetime) {
      // If user state is undefined after initial load, treat as not logged in
      if (isLoggedIn === undefined) {
        console.log('PaymentScreen: User state undefined, waiting briefly...');
        setIsCheckingAuth(true);

        // Short timeout - if still undefined after 1 second, treat as not logged in
        const timeout = setTimeout(() => {
          localStorage.setItem('lifetimeDealIntent', 'true');
          setIsCheckingAuth(false);
          navigate('/sign-up');
        }, 1000); // 1 second timeout

        return () => clearTimeout(timeout);
      }

      if (isLoggedIn === false) {
        // Store the lifetime intent and redirect to signup
        localStorage.setItem('lifetimeDealIntent', 'true');
        setIsCheckingAuth(false); // Clear loader before redirect
        navigate('/sign-up');
      } else if (isLoggedIn === true) {
        // User is logged in, clear any stored intent and proceed with lifetime payment
        localStorage.removeItem('lifetimeDealIntent');
        setIsCheckingAuth(false);
      }
    } else {
      setIsCheckingAuth(false);
    }
  }, [isLifetime, isLoggedIn, navigate, user, authState]);
  // const status = searchParams?.get('status') || '';
  const location = useLocation();
  const status = location?.state?.status;

  // Check if user came with a pre-selected plan
  const hasPreSelectedPlan =
    location?.state?.subscriptionId || localStorage.getItem('selectedPlan');

  const [cardModalOpen, setCardModalOpen] = useState(
    trial && !hasPreSelectedPlan,
  );

  // Auto-open card form for lifetime payments when no cards exist
  useEffect(() => {
    if (isLifetime && paymentMethods?.data !== undefined) {
      const hasNoCards = !paymentMethods?.data || paymentMethods.data.length === 0;
      if (hasNoCards) {
        setCardModalOpen(true);
      }
    }
  }, [isLifetime, paymentMethods?.data]);

  const planId = location?.state?.subscriptionId || productData.id;

  const { plan_name: planName, stripe_price: stripePrice } =
    currentSubscription || {};
  const { interval, amount } = stripePrice || {};

  const updateSelectedPlanType = () => {
    let newPlan = null;

    // Handle lifetime subscription differently
    if (isLifetime) {
      // For lifetime, the plan will be loaded by the useEffect hook
      // Skip the regular plan loading logic
      return;
    }

    const plans =
      subscriptionPlans[
        selectedPlanType === 'month' ? 'monthlyPlan' : 'annualplan'
      ];
    const allPlans = (subscriptionPlans.monthlyPlan || []).concat(
      subscriptionPlans.annualplan || [],
    );

    if (selectedPlanId) {
      if (
        newSubscription &&
        newSubscription?.interval !== selectedPlanType &&
        newSubscription?.id === parseInt(selectedPlanId)
      ) {
        newPlan = plans?.find((plan) => plan.type === newSubscription?.type);
        setNewSubscription(newPlan);
      } else {
        newPlan = allPlans.find((plan) => {
          return plan.id === parseInt(selectedPlanId);
        });
        if (newPlan?.interval !== selectedPlanType) {
          setSelectedPlanType(newPlan?.interval);
        }
        setNewSubscription(newPlan);
      }
    } else if (!currentSubscription) {
      // If no current subscription, find Pro Plan or any available plan
      newPlan = plans?.find(
        (plan) => plan.type === 'Pro Plan' || plan.type === 'Pro',
      );

      // Fallback to any available plan if Pro plan not found
      if (!newPlan && plans && plans.length > 0) {
        newPlan = plans[0]; // Use first available plan as fallback
      }

      if (newPlan) {
        setNewSubscription(newPlan);
        setSelectedPlanId(newPlan.id);
      }
    } else {
      newPlan = plans?.find(
        (plan) => plan.type === currentSubscription?.plan_name,
      );
      setNewSubscription(newPlan);
    }

    setPlanFeatures(newPlan?.services);
  };
  useEffect(() => {
    if (Object.keys(subscriptionPlans).length > 0 || isLifetime) {
      updateSelectedPlanType();
    }
  }, [selectedPlanType, subscriptionPlans, selectedPlanId, isLifetime]);

  useEffect(() => {
    if (trial) {
      dispatch(resetOnboarding());
    }
  }, [trial, dispatch]);

  // Load lifetime plan data when in lifetime mode
  useEffect(() => {
    const loadLifetimePlanData = async () => {
      if (isLifetime) {
        try {
          const response = await getAvailableLifetimePlans();
          const lifetimePlans = response?.data?.lifetime_plans || [];
          if (lifetimePlans.length > 0) {
            // Get plan type from URL params (defaults to 'pro')
            const planType = searchParams?.get('plan');

            // Find the matching lifetime plan
            let lifetimePlan;
            if (planType === 'business') {
              lifetimePlan = lifetimePlans.find(p =>
                p.product_name?.toLowerCase().includes('business')
              );
            } else {
              lifetimePlan = lifetimePlans.find(p =>
                p.product_name?.toLowerCase().includes('pro') &&
                !p.product_name?.toLowerCase().includes('business')
              );
            }

            // Fallback to first plan if no match found
            lifetimePlan = lifetimePlan || lifetimePlans[0];

            const defaultProServices = [
              '100 Active Projects',
              '100 Brand Voices',
              '10 Team Members',
              'Platform Integrations',
              '100 Keywords',
              'All Pro Features',
              'Lifetime Access',
            ];

            const defaultBusinessServices = [
              'Everything in Lifetime Pro',
              '4 Vocable Accounts',
              'Advanced Collaboration',
              'Share Brand Voices',
              'Link Sharing with Clients',
              'Advanced Brand Intelligence',
              'Lifetime Access',
            ];

            const planData = {
              id: lifetimePlan.id,
              stripe_price_id: lifetimePlan.stripe_price_id,
              type: lifetimePlan.product_name || (planType === 'business' ? 'Lifetime Business Plan' : 'Lifetime Pro Plan'),
              amount: lifetimePlan.amount,
              interval: 'lifetime',
              services: lifetimePlan.services || (planType === 'business' ? defaultBusinessServices : defaultProServices),
            };
            setNewSubscription(planData);
            setPlanFeatures(planData.services);
          }
        } catch (error) {
          console.error('Error loading lifetime plan data:', error);
        }
      }
    };

    loadLifetimePlanData();
  }, [isLifetime, searchParams]);

  const fetchPlans = useCallback(async (setShouldRedirect) => {
    fetchPlansData({
      setPaymentMethods,
      setSubscriptionPlans,
      setCurrentSubscription,
      setSelectedPlanType,
      setLastSubscription,
      setShouldRedirect,
    });
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    if (planId) {
      setSelectedPlanId(planId);
    }
  }, [planId]);

  // Check for stored plan data from sign-up process
  useEffect(() => {
    const storedPlan = localStorage.getItem('selectedPlan');

    if (storedPlan && !currentSubscription) {
      try {
        const planData = JSON.parse(storedPlan);

        // Set the selected plan based on stored data
        setSelectedPlanId(planData.id);

        // Set the plan type (monthly/yearly) based on stored data
        if (planData.duration === 'year') {
          setSelectedPlanType('year');
        } else {
          setSelectedPlanType('month');
        }

        // Clear the stored plan data
        localStorage.removeItem('selectedPlan');
      } catch (error) {
        console.error('Error parsing stored plan data:', error);
        localStorage.removeItem('selectedPlan');
      }
    }
  }, [currentSubscription]);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      dispatch(
        setLoader({
          isLoading: true,
          heading: 'Fetching Billing Info',
          description: 'Please wait...',
        }),
      );
      const paymentMethodsRes = await getPaymentMethods();
      const { data: paymentMethodData } = paymentMethodsRes || {};
      const { payment_methods, default_payment_method } =
        paymentMethodData || {};
      setPaymentMethods(payment_methods);
      setSelectedCard(default_payment_method);
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
    } catch (error) {
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
    }
  }, [dispatch]);

  const fetchInvoices = useCallback(async () => {
    try {
      dispatch(
        setLoader({
          isLoading: true,
          heading: 'Fetching Invoices',
          description: 'Please wait...',
        }),
      );
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
    } catch (error) {
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
    }
  }, [dispatch]);

  const fetchCurrentSubscription = useCallback(async () => {
    try {
      const currentSubscriptionRes = await getCurrentSubscription();
      const { data: subscriptionData } = currentSubscriptionRes || {};
      const { current_subscription: currentSubscription } =
        subscriptionData || {};
      // const { last_subscription: lastSubscription } =
      // subscriptionData || {};

      setCurrentSubscription(currentSubscription);
      // setLastSubscription(lastSubscription);
    } catch (error) {}
  }, []);

  const confirmSubscription = useCallback(async () => {
    try {
      await refreshSubscription(paymentIntent);
      toast.success('Plan subscribed successfully!');
      fetchPaymentMethods();
      fetchCurrentSubscription().then(() => setShowSuccessPage(true));
    } catch (error) {}
  }, [fetchCurrentSubscription, fetchPaymentMethods, paymentIntent]);

  useEffect(() => {
    paymentIntent && confirmSubscription();
  }, [confirmSubscription, paymentIntent]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    fetchCurrentSubscription();
  }, [fetchCurrentSubscription]);

  useEffect(() => {
    if (paymentMethods?.data?.length === 1) {
      setShowMore(1);
    }
  }, [paymentMethods?.data?.length]);
  useEffect(() => {
    showSuccessPage &&
      planName &&
      amount &&
      interval &&
      navigateToSuccessPage();
  }, [planName, amount, interval, showSuccessPage]);

  const navigateToSuccessPage = () =>
    navigate(
      `/${CONFIRM_SUBSCRIPTION}/plan/${planName}/price/${amount}/type/${interval}`,
    );

  const { data: paymentMethodData } = paymentMethods || {};
  const viewAllButtonCursor =
    paymentMethodData?.length > 1 ? 'pointer' : 'default';

  const handlePlanSelect = (plan) => {
    // Close the modal first
    setIsModalOpen(false);

    // Update the selected plan directly in the current component state
    if (plan && plan.id) {
      setSelectedPlanId(plan.id);
      setNewSubscription(plan);

      // Handle different interval field names (interval vs billing_interval)
      const planInterval = plan.interval || plan.billing_interval || 'month';
      setSelectedPlanType(planInterval);

      // Handle different services field names (services vs features)
      const planServices = plan.services || plan.features || [];
      setPlanFeatures(planServices);
    } else {
      console.error('Invalid plan object received:', plan);
    }
  };

  // if (!currentSubscription) {
  //   return null;
  // }

  const handleDefault = async (cardId) => {
    try {
      dispatch(
        setLoader({
          isLoading: true,
          heading: 'Marking card as default!',
          description: 'Please wait...',
        }),
      );
      await markDefaultCard(cardId);
      setSelectedCard(cardId);
      fetchCurrentSubscription();
      fetchPaymentMethods();
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
    } catch (error) {
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
    }
  };

  const handleRemove = async (cardId) => {
    try {
      dispatch(
        setLoader({
          isLoading: true,
          heading: 'Removing Card!',
          description: 'Please wait...',
        }),
      );
      await removeCard(cardId);
      fetchCurrentSubscription();
      fetchPaymentMethods();
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
    } catch (error) {
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'unpaid':
        return 'Your payment is failed. Please update your payment method.';
      case 'canceled':
        return 'Your subscription has been canceled. Please resubscribe.';
      case 'incomplete':
        return 'Your subscription is incomplete. Please complete the necessary steps to activate your subscription.';
      default:
        return '';
    }
  };

  const transformedPaymentMethodData =
    paymentMethodData?.reduce((acc, currentValue) => {
      if (currentValue.id === selectedCard) {
        acc.unshift(currentValue);
      } else {
        acc.push(currentValue);
      }
      return acc;
    }, []) || [];

  // Show loading state while checking authentication for lifetime payments
  if (isCheckingAuth) {
    return (
      <div className={sc.billing__root}>
        <OnboardingHeader />
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '50vh' }}
        >
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Checking authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={sc.billing__root}>
      <OnboardingHeader />
      {status && (
        <div className={sc.billing__root__header}>
          <span>
            <TbExclamationCircle />
          </span>
          <p>
            {(status === 'unpaid' ||
              status === 'canceled' ||
              status === 'incomplete') && (
              <span>{getStatusMessage(status)}</span>
            )}
          </p>
        </div>
      )}

      <div className={sc.outer_container}>
        <div
          className={`${sc.planTypeCardWrapper} ${sc.utility_width_plan_detail}`}
        >
          <PlanDetailCard
            selectedPlanType={selectedPlanType}
            setSelectedPlanType={setSelectedPlanType}
            plan={newSubscription}
            isDisabledSwitch={isDisabledSwitch}
            isTrial={trial}
            setIsModalOpen={setIsModalOpen}
            isLifetime={isLifetime}
          />
          <TrialFeatures features={planFeatures} />
          <div className={`${sc.addedCardsWrapper} w-100`}>
            <p className={`${sc.heading_billing_payment}`}>PAYMENT DETAILS</p>
            <div className={sc.addedCardsArea}>
              <div className={sc.paymentMethodCardsWrapper}>
                {[...transformedPaymentMethodData?.slice(0, showMore)]?.map(
                  (card) => (
                    <PaymentCard
                      key={card.id}
                      card={card}
                      setSelectedCard={(card) => handleDefault(card.id)}
                      checked={card.id === selectedCard}
                      selectedCard={selectedCard}
                      setShowMore={setShowMore}
                      handleRemove={handleRemove}
                    />
                  ),
                )}
              </div>
              <div className={sc.addPaymentActionWrapper}>
                {showMore === 1 && (
                  <button
                    disabled={
                      paymentMethodData?.length === 1 ||
                      !paymentMethodData?.length
                    }
                    onClick={() => setShowMore(paymentMethodData?.length)}
                    className={sc.viewAllBtn}
                    style={{ cursor: viewAllButtonCursor }}
                  >
                    <span>View All</span>
                    <img src="/Images/chevron-down.svg" alt="downArrow" />
                  </button>
                )}
                {showMore > 1 && (
                  <button
                    onClick={() => setShowMore(1)}
                    className={sc.viewLessBtn}
                  >
                    <span>View Less</span>
                    <img src="/Images/chevron-down.svg" alt="downArrow" />
                  </button>
                )}
              </div>
              {/* <div className={sc.addNewPaymentMethodBtnWrapper}>
      <button
      className={sc.addNewPaymentMethodBtn}
      onClick={() => setCardModalOpen(true)}
      >
      <img src="/Images/plus-grey1.svg" alt="addIcon" />
      <span className={sc.newMethodText}>
      Add New Payment Method
      </span>
      </button>
      </div> */}
              {!cardModalOpen ? (
                <div className={sc.addNewPaymentMethodBtnWrapper}>
                  <button
                    className={sc.addNewPaymentMethodBtn}
                    onClick={() => setCardModalOpen(true)}
                  >
                    <img src="/Images/plus-grey1.svg" alt="addIcon" />
                    <span className={sc.newMethodText}>
                      Add New Payment Method
                    </span>
                  </button>
                </div>
              ) : (
                <SubscriptionCardForm
                  onHide={() => setCardModalOpen(false)}
                  show={cardModalOpen}
                  isProductView={true}
                  refetch={fetchPaymentMethods}
                  product={productData}
                  // initiateTrial={initiateTrial}
                  isTrial={trial}
                />
              )}
            </div>
            <div className="mt-3">
              {/* <CustomCheckbox label="I need an invoice" /> */}
            </div>
          </div>
        </div>
        <div className={`${sc.utility_width_order_summary}`}>
          <OrderSummary
            newSubscription={newSubscription}
            setIsDisabledSwitch={setIsDisabledSwitch}
            refetch={fetchPlans}
            isTrial={trial}
            product={productData}
            setOrderSummary={setOrderSummary}
            paymentMethodData={paymentMethodData}
            planId={planId}
            selectedPlanType={selectedPlanType}
            isLifetime={isLifetime}
          />
        </div>
      </div>
      {/* <SubscriptionCardModal
        onHide={() => setCardModalOpen(false)}
        show={cardModalOpen}
        isProductView={false}
        refetch={fetchPaymentMethods}
      /> */}
      <BottomDrawer
        title="Subscription Plan"
        show={isModalOpen}
        placement="bottom"
      >
        <SubscriptionScreen
          closeDrawer={() => setIsModalOpen(false)}
          subscriptionPlans={subscriptionPlans}
          paymentMethods={paymentMethods}
          currentSubscription={currentSubscription}
          lastSubscription={lastSubscription}
          refetch={fetchPlans}
          handlePlanSelect={handlePlanSelect}
          hideTestimonials={true} // Hide testimonials in modal/drawer view
        />
      </BottomDrawer>
    </div>
  );
};

export default PaymentScreen;
